/**
 * Vuex module for trip-related operations
 */

export const namespaced = true;

const initState = () => {
  return {
    isLoading: false,
    trips: [],
  };
};

export const state = initState();

export const mutations = {
  SET_LOADING(state, status) {
    state.isLoading = status;
  },
  SET_TRIPS(state, trips) {
    state.trips = trips;
  },
};

export const actions = {
  /**
   * Pauses a trip using REST API
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {Object} options.pauseData - Data for pausing the trip
   * @returns {Promise<Object>} - Result of the pause operation
   */
  async pauseTrip({ commit }, { pauseData }) {
    try {
      commit("SET_LOADING", true);

      // Use REST API to pause the trip
      const restClient = require("../../lib/restClient").default;
      const response = await restClient.put(
        `/api/v1/trips/${pauseData.tripId}/pause`,
        {
          reactivationDate: pauseData.reactivationDate,
          comment: pauseData.comment,
          pausedAt: pauseData.pausedAt,
          newEndDate: pauseData.newEndDate,
          reservedCredits: pauseData.reservedCredits,
        }
      );

      return response;
    } catch (error) {
      console.error("Error pausing trip:", error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
  /**
   * Activates a trip using REST API
   * @param {Object} context - Vuex action context
   * @param {Function} context.commit - Commit mutation
   * @param {Object} options - Options object
   * @param {Object} options.tripData - Data for activating the trip
   * @returns {Promise<Object>} - Result of the activation operation
   */
  async activateTrip({ commit }, { tripData }) {
    try {
      commit("SET_LOADING", true);

      const restClient = require("../../lib/restClient").default;
      let response;

      // Check if it's a reactivation (existing trip) or a new trip
      if (tripData.tripId) {
        // Reactivate an existing trip (paused)
        response = await restClient.put(
          `/api/v1/trips/${tripData.tripId}/activate`,
          {
            programId: tripData.programId,
            description: tripData.description,
            startDate: tripData.startDate,
            durationMonths: tripData.durationMonths,
            endDate: tripData.endDate,
          }
        );
      } else {
        // Create a new trip
        response = await restClient.post("/api/v1/trips", {
          userId: tripData.userId,
          programId: tripData.programId,
          description: tripData.description,
          startDate: tripData.startDate,
          durationMonths: tripData.durationMonths,
          endDate: tripData.endDate,
        });
      }

      return response;
    } catch (error) {
      console.error("Error activating trip:", error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async changeTripStatus({ commit }, { changeData }) {
    try {
      commit("SET_LOADING", true);
      const restClient = require("../../lib/restClient").default;

      const response = await restClient.post(`/api/v1/tripState/change-state`, {
        tripId: changeData.tripId,
        newState: changeData.newState,
        reason: changeData.reason,
        trigger: changeData.trigger,
        changedBy: changeData.changedBy,
      });

      return response;
    } catch (error) {
      console.error("Error when changing trip status:", error);
      return {
        success: false,
        error: error.message || "Error changing trip status",
      };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async restoreTrip({ commit }, { data }) {
    try {
      commit("SET_LOADING", true);
      const restClient = require("../../lib/restClient").default;

      const response = await restClient.put(`/api/v1/trips/${data.tripId}/restore`, {
        invitationDate: data.invitationDate,
        amount: data.amount,
        changedBy: data.changedBy,
        newState: data.newState,
      });

      return response;
    } catch (error) {
      console.error("Error when restoring trip:", error);
      return { success: false, error: error.message || "Error restoring trip" };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async scheduleTrip({ commit }, { data }) {
    try {
      commit("SET_LOADING", true);
      const restClient = require("../../lib/restClient").default;

      const response = await restClient.put(
        `/api/v1/trips/${data.tripId}/reactivate`,
        {
          invitationDate: data.invitationDate,
          amount: data.amount,
          changedBy: data.changedBy,
        }
      );

      return response;
    } catch (error) {
      console.error("Error when scheduling trip:", error);
      return { success: false, error: error.message || "Error scheduling trip" };
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async editCredits({ commit }, { editData, restClient }) {
    try {
      const response = await restClient.post(
        `/api/v1/membership/movements/account-cross-credits`,
        {
          ...editData,
        }
      );
      return response;
    } catch (error) {
      console.error("Error editing trip credits:", error);
      throw error;
    }
  },
  async manageMultipleTrips({ commit }, { manageData }) {
    try {
      const restClient = require("../../lib/restClient").default;
      const response = await restClient.put(`/api/v1/trips/manage`, {
        ...manageData,
      });
      return response;
    } catch (error) {
      console.error("Error managing multiple trips:", error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },
};

export const getters = {
  isLoading: (state) => state.isLoading,
  trips: (state) => state.trips,
};
